import { useTranslate } from "ra-core";
import { Input } from "@/components/ui/input";
import type { Delay } from "../../../../supabase/functions/_shared/automation/types.ts";
import { selectClass } from "./ConditionBuilder";

type Mode = "now" | "business_days" | "minutes" | "hours" | "before" | "from";
const MODES: readonly Mode[] = [
  "now",
  "business_days",
  "minutes",
  "hours",
  "before",
  "from",
];
const modeOf = (d: Delay | undefined): Mode =>
  !d || typeof d !== "object"
    ? "now"
    : "business_days" in d
      ? "business_days"
      : "before" in d
        ? "before"
        : "from" in d
          ? "from"
          : "hours" in d
            ? "hours"
            : "minutes";

const num = (v: unknown, fallback: number) =>
  typeof v === "number" && Number.isFinite(v) ? v : fallback;

/** Delay picker (spec 05 §7). Existing `before`/`from` sources are preserved when editing. */
export const DelayPicker = ({
  value,
  onChange,
  allowNow = true,
  label,
}: {
  value: Delay | undefined;
  onChange: (d: Delay | undefined) => void;
  allowNow?: boolean;
  label?: string;
}) => {
  const t = useTranslate();
  const mode = modeOf(value);
  const v = (value ?? {}) as Record<string, unknown>;
  const name = label ?? t("gavion.automation.delay.label");
  const before = typeof v.before === "string" ? v.before : "call.demo_at";
  const from = typeof v.from === "string" ? v.from : "call.callback_at";
  const setMode = (m: Mode) =>
    onChange(
      m === "now"
        ? undefined
        : m === "business_days"
          ? { business_days: 2, at: "10:00" }
          : m === "minutes"
            ? { minutes: 30 }
            : m === "hours"
              ? { hours: 1 }
              : m === "before"
                ? { before, hours: 24 }
                : { from },
    );
  const nLabel = `${name} — ${t("gavion.automation.delay.n")}`;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        aria-label={name}
        className={selectClass}
        value={mode}
        onChange={(e) => setMode(e.target.value as Mode)}
      >
        {MODES.filter((m) => allowNow || m !== "now").map((m) => (
          <option key={m} value={m}>
            {t(`gavion.automation.delay.${m}`)}
          </option>
        ))}
      </select>
      {mode === "business_days" && (
        <>
          <Input
            aria-label={nLabel}
            type="number"
            min={0}
            max={366}
            step={1}
            className="w-20"
            value={num(v.business_days, 0)}
            onChange={(e) =>
              onChange({
                business_days: Number(e.target.value),
                at: String(v.at ?? "10:00"),
              })
            }
          />
          <Input
            aria-label={`${name} — ${t("gavion.automation.delay.at")}`}
            type="time"
            className="w-28"
            value={String(v.at ?? "10:00")}
            onChange={(e) =>
              onChange({
                business_days: num(v.business_days, 0),
                at: e.target.value,
              })
            }
          />
        </>
      )}
      {mode === "minutes" && (
        <Input
          aria-label={nLabel}
          type="number"
          className="w-24"
          value={num(v.minutes, 0)}
          onChange={(e) => onChange({ minutes: Number(e.target.value) })}
        />
      )}
      {(mode === "hours" || mode === "before") && (
        <Input
          aria-label={nLabel}
          type="number"
          className="w-24"
          value={num(v.hours, 0)}
          onChange={(e) =>
            onChange(
              mode === "hours"
                ? { hours: Number(e.target.value) }
                : { before, hours: Number(e.target.value) },
            )
          }
        />
      )}
    </div>
  );
};
